import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  NOTICE_TEXT,
  acceptConsent,
  ensureConsentResolved,
  needsConsentPrompt,
  rejectConsent,
  savePreferences,
  COOKIE_SETTINGS_EVENT,
} from "@/lib/consent";

/**
 * Cookie consent banner.
 *
 * Shown only to visitors in regions that require consent (and to anyone whose
 * region could not be resolved). Refusing takes the same single click as
 * accepting, and the choice can be reopened later from the footer's
 * "Cookie settings" link.
 */
export const CookieConsentBanner = () => {
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [advertising, setAdvertising] = useState(true);

  useEffect(() => {
    let active = true;
    void ensureConsentResolved().finally(() => {
      if (!active) return;
      setReady(true);
      if (needsConsentPrompt()) setOpen(true);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const reopen = () => {
      setManage(true);
      setOpen(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  if (!ready || !open) return null;

  const close = () => {
    setOpen(false);
    setManage(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-notice"
      className="fixed inset-x-0 bottom-0 z-[100] px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-xl border bg-card/95 p-5 shadow-elevated backdrop-blur">
        <h2 className="text-base font-semibold text-foreground">Your cookie choices</h2>
        <p id="cookie-consent-notice" className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {NOTICE_TEXT}
        </p>

        {manage && (
          <div className="mt-4 rounded-lg border bg-muted/40 p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="cookie-advertising"
                checked={advertising}
                onCheckedChange={(checked) => setAdvertising(checked === true)}
              />
              <div className="grid gap-1">
                <Label htmlFor="cookie-advertising" className="cursor-pointer text-sm font-medium">
                  Advertising &amp; measurement
                </Label>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Loads the Meta (Facebook and Instagram) and TikTok tags so we can count visits
                  and see how our ads perform. Turned off if you refuse.
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3">
              <span className="mt-0.5 h-4 w-4 rounded-full border border-primary bg-primary/10" aria-hidden="true" />
              <div className="grid gap-1">
                <span className="text-sm font-medium">Strictly necessary</span>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Keeps your cart and sign-in working. Always on, no consent needed.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Button
            onClick={() => {
              acceptConsent();
              close();
            }}
            className="w-full sm:w-auto"
          >
            Accept all
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              rejectConsent();
              close();
            }}
            className="w-full sm:w-auto"
          >
            Reject all
          </Button>
          {manage ? (
            <Button
              variant="secondary"
              onClick={() => {
                savePreferences(advertising);
                close();
              }}
              className="w-full sm:w-auto"
            >
              Save preferences
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setManage(true)} className="w-full sm:w-auto">
              Manage preferences
            </Button>
          )}
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground sm:ml-auto"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};
